import { ARTIFICER, FORKS, GHOST_GUARD, GHOST_RESCUE, HEROES, NIGHTMARES, PURIFY_RANGE, SKILLS } from '@somnhold/content';
import { hexLen, hexToWorld } from '../hex.ts';
import { innerStep } from '../walls.ts';
import { dist2, idiv, imin, len, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Command, Enemy, Hero } from '../types.ts';
import { countNear, enemiesNear, firstNear, isFlying, targetable } from '../battle/combat.ts';
import { heroHome, postSpot } from '../battle/create.ts';
import { orderMove } from '../battle/heroes.ts';
import { corrupt, isMelee } from '../battle/kits.ts';
import { castSkill, kitOf, mostHurt, overloadAt, rankedSkill } from '../battle/skills.ts';
import { relicUses } from '../battle/gear.ts';
import { structurePos } from '../battle/combat.ts';
import { dragonGhost, fireOnDragon } from './dragon.ts';

/**
 * Ghost behavior (SPEC §11): a small deterministic state machine driven by the learned profile.
 * Ghosts never use their ultimate unless they carry the Vigil Grimoire (once per wave).
 */
/** After fighting its way out of a corner, a ghost doesn't retreat again for this long. */
const RETREAT_PAUSE = 12 * 12;

export const ghostThink = (s: BattleState, h: Hero): void => {
  if (h.downTicks > 0 || h.stun > 0) return;
  // A cleric heals as it goes: walking, dodging or on its way to a fallen friend (mend is cast from afar).
  if (h.cls === 'cleric') clericCare(s, h, true);
  // A paladin raises its aegis where it is needed, fighting or not.
  if (h.cls === 'paladin' && h.skillCd[1] === 0) guard(s, h);
  // The dragon's clearing: off its glass and its burns, and to a harpoon's lever when it pays (ai/dragon.ts).
  if (s.rules?.dragon && dragonGhost(s, h)) return;
  // Ascuas: out of a marked eruption or off a lava crack first, like anyone with eyes would.
  if (dodge(s, h)) return;
  const p = h.profile;
  const post = guardSpot(s, h);
  const hpBp = h.stats.maxHp > 0 ? Math.trunc((h.hp * 10000) / h.stats.maxHp) : 0;

  if (h.ai.mode === 'retreat') {
    const home = heroHome(s, h.post);
    const cornered = withinRange(h.x, h.y, home.x, home.y, 400) && s.enemies.some((e) => e.hp > 0 && e.tgtKind === 1 && e.tgtId === h.id);
    if (hpBp >= 7000 || s.enemies.length === 0 || cornered) {
      // Back home and still hunted: no more running, fight back for a while (it can't heal under blows).
      h.ai.mode = 'hold';
      if (cornered) h.ai.t = s.tick + RETREAT_PAUSE;
    } else {
      if (!withinRange(h.x, h.y, home.x, home.y, 400) && (!h.moving || s.tick % 24 === 0)) orderMove(s, h, home.x, home.y);
      h.target = 0;
      return;
    }
  } else if (hpBp < (p.retreatHpBp < 4000 ? p.retreatHpBp : 4000) && h.invuln === 0 && s.tick >= h.ai.t) {
    h.ai.mode = 'retreat';
    h.target = 0;
    return;
  }

  // A friend fell or is about to: the Vigil Grimoire's one ultimate goes there, if it pays.
  if (rescue(s, h, post)) return;

  // A cursed tower near our post goes silent until someone stands by it for 3 s (the Ash Witch).
  const cursed = nearestCursed(s, post.x, post.y, p.gateLeash + 3000);
  if (cursed) {
    if (!withinRange(h.x, h.y, cursed.x, cursed.y, PURIFY_RANGE - 200)) {
      if (!h.moving || s.tick % 12 === 0) orderMove(s, h, cursed.x + 400, cursed.y);
      h.target = 0;
      return;
    }
    // Standing still purifies; keep shooting whatever is in range meanwhile.
    h.moving = false;
    h.path = [];
    return;
  }

  // Where the nightmares run for the node (crystal sites), the ghost guards a wider ring around it.
  // At the bastion the learned leash is capped: a ghost meets them at the walls, not out in the woods.
  const gateLeash = s.map.kind === 'bastion' ? imin(p.gateLeash, GHOST_GUARD.maxLeash) : p.gateLeash;
  const leash = gateLeash + h.stats.range + (s.rules?.rush ? 2500 : 0);
  const candidates = enemiesNear(s, post.x, post.y, leash, (e) => targetable(s, e) && e.kind !== 'boneWall');
  const reachable = isMelee(h) ? candidates.filter((e) => !isFlying(e)) : candidates;
  // A barbarian goes out after the ones that shoot or batter from afar, and the elites, unless it would be alone against a crowd.
  const prey = h.cls === 'barbarian' ? hunt(s, h, post, leash) : null;
  // Every ghost goes for the pack's support first: a mourner mending it, a bonesinger raising it, a bannerman driving it.
  const mender = nearestWith(h, reachable, (e) => {
    const d = NIGHTMARES[e.kind];
    return !!(d.mends || d.raises || d.banner || d.wards);
  });
  // Little at the walls (the towers can hold a couple): a siege engine throwing at them from afar is worth the walk out.
  let pressing = 0;
  for (const e of candidates) if (!NIGHTMARES[e.kind].lobs) pressing++;
  const siege = pressing <= GHOST_GUARD.siegeWhile ? siegeOut(s, h, post) : null;
  // One of the Unsleeping channelling his ultimate near: everyone on him, to cut it (docs/ANTIHEROES.md).
  const channel = nearestWith(h, candidates, (e) => (e.un?.cast ?? 0) > 0);
  const target = channel ?? prey ?? mender ?? siege ?? pick(h, reachable.length > 0 ? reachable : candidates, p.targetPriority);

  if (!target) {
    h.ai.mode = 'hold';
    h.target = 0;
    if (!withinRange(h.x, h.y, post.x, post.y, 350) && (!h.moving || s.tick % 36 === 0)) orderMove(s, h, post.x, post.y);
    useIdleSkills(s, h);
    return;
  }
  h.ai.mode = 'engage';
  useSkills(s, h, target);
  // Keep the preferred distance: ranged ghosts hold position if the target is already in range.
  if (withinRange(h.x, h.y, target.x, target.y, h.stats.range)) {
    h.target = target.id;
    h.moving = false;
    h.path = [];
    return;
  }
  const pref = p.preferredRange > h.stats.range ? h.stats.range : p.preferredRange;
  if (isMelee(h) || pref < 1200) {
    h.target = target.id;
    return;
  }
  // Ranged: step toward the target only until it is in range.
  h.target = target.id;
};

/** Steps out of a marked eruption (or off lava) to the nearest edge plus a little. True while doing so. */
const dodge = (s: BattleState, h: Hero): boolean => {
  if (s.rules?.tier !== 2 && s.rules?.tier !== 3) return false;
  for (const z of s.zones) {
    // Ascuas: marked eruptions and lava; Obsidiana: cracked glass, its pits and shards.
    const danger = (z.kind === 'eruption' && (z.warn ?? -1) > 0) || z.kind === 'lava' || z.kind === 'glass' || z.kind === 'shards';
    if (!danger || !withinRange(h.x, h.y, z.x, z.y, z.radius + 150)) continue;
    let dx = h.x - z.x;
    let dy = h.y - z.y;
    const d = len(dx, dy);
    if (d === 0) {
      dx = 1;
      dy = 0;
    }
    const out = z.radius + 450;
    const k = d === 0 ? out : idiv(out * 1000, d);
    const tx = z.x + (d === 0 ? out : idiv(dx * k, 1000));
    const ty = z.y + (d === 0 ? 0 : idiv(dy * k, 1000));
    if (!h.moving || s.tick % 6 === 0) orderMove(s, h, tx, ty);
    h.target = 0;
    return true;
  }
  return false;
};

const nearestCursed = (s: BattleState, x: number, y: number, r: number): { x: number; y: number } | null => {
  let best: { x: number; y: number } | null = null;
  let bd = r * r;
  for (const st of s.structures) {
    if ((!st.cursed && !((st.cocoon ?? 0) > 0)) || st.destroyed) continue;
    const c = hexToWorld(st.q, st.r);
    const d = dist2(x, y, c.x, c.y);
    if (d <= bd) {
      bd = d;
      best = c;
    }
  }
  return best;
};

/** How far around a spot the nightmares count toward its danger. */
const THREAT_R = 4500;
/** A ghost looks again for where it is needed this often (ticks). */
const RETHINK = 24;

/**
 * Where the ghost stands guard: wherever the bastion is hurting most right now — a gate under
 * pressure, a battered or broken stretch of wall, the keep — weighed by the nightmares around it
 * (warlords and wall-breakers count more) and shared out so ghosts don't all pile on one spot. With
 * nothing going on it keeps its own post. Re-picked every two seconds, and only for a clearly worse
 * danger, so it doesn't dither.
 *
 * A ghost posted at a gate does what its player would: it only goes to help elsewhere while nothing is
 * coming down its own road, and heads back to its side (its gate, its stretch of wall, the keep) as soon
 * as something is. One sent "wherever needed" weighs the whole bastion all the time.
 */
const guardSpot = (s: BattleState, h: Hero): { x: number; y: number } => {
  if (s.map.kind === 'dungeon') return postFor(s, h);
  // The dragon's clearing: its own post (by a harpoon, or the middle).
  if (s.rules?.dragon) return postSpot(s, h.post);
  // A saga escort: keep by the caravan, a step ahead of it on its road, spread around it.
  if (s.escort) {
    const cart = s.bots?.find((b) => b.id === s.escort?.cart);
    if (cart) {
      const to = s.escort.road[s.escort.next] ?? cart;
      const i = s.heroes.indexOf(h);
      return { x: cart.x + idiv((to.x - cart.x) * 2, 3) + ((i % 3) - 1) * 700, y: cart.y + idiv((to.y - cart.y) * 2, 3) + (i > 2 ? 700 : 0) };
    }
  }
  // Crystal sites: they run for the node, so meet them at the node.
  if (s.rules?.rush) return heroHome(s, -1);
  if (h.ai.gx !== undefined && h.ai.gy !== undefined && s.tick - (h.ai.at ?? 0) < RETHINK) return { x: h.ai.gx, y: h.ai.gy };
  const own = !h.roam && s.map.sectors[h.post] && s.enemies.some((e) => e.hp > 0 && e.sector === h.post && e.kind !== 'boneWall') ? h.post : -1;
  const spots: { x: number; y: number }[] = s.map.sectors.filter((sec) => own < 0 || sec.index === own).map((sec) => postSpot(s, sec.index));
  const k = s.map.bastion;
  for (const st of s.structures) {
    if (st.type !== 'rampart' || (!st.destroyed && st.hp >= st.maxHp)) continue;
    if (own >= 0 && nearestGate(s, st.q, st.r) !== own) continue;
    // Just inside the wall, where a hero can stand and meet what comes through.
    const c = hexToWorld(st.q, st.r);
    if (hexLen(st.q, st.r) === k) spots.push({ x: idiv(c.x * (k - 1), k), y: idiv(c.y * (k - 1), k) });
    else {
      const inner = innerStep(s.map, st.q, st.r);
      spots.push(hexToWorld(inner.q, inner.r));
    }
  }
  spots.push(heroHome(s, -1));
  let best = -1;
  let bestScore = 0;
  let curScore = 0;
  spots.forEach((sp, i) => {
    let score = 0;
    for (const e of enemiesNear(s, sp.x, sp.y, THREAT_R, (x) => x.hp > 0 && x.kind !== 'boneWall')) score += e.elite ? 3 : e.tgtKind === 2 ? 2 : 1;
    if (score === 0) return;
    // Shared out: every other ghost already guarding here halves what's left for this one.
    let others = 0;
    for (const o of s.heroes) {
      if (o.id === h.id || o.control === 'live' || o.downTicks > 0 || o.ai.gx === undefined || o.ai.gy === undefined) continue;
      if (withinRange(o.ai.gx, o.ai.gy, sp.x, sp.y, 1500)) others++;
    }
    score = idiv(score * 1000, 1 + others);
    if (h.ai.gx !== undefined && h.ai.gy !== undefined && withinRange(h.ai.gx, h.ai.gy, sp.x, sp.y, 1500)) curScore = score;
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  });
  let spot = best >= 0 ? (spots[best] as { x: number; y: number }) : postFor(s, h);
  // Stay put unless the new danger is clearly worse than where it already is (called home, it goes).
  if (best >= 0 && curScore > 0 && bestScore * 2 < curScore * 3 && h.ai.gx !== undefined && h.ai.gy !== undefined) spot = { x: h.ai.gx, y: h.ai.gy };
  h.ai.gx = spot.x;
  h.ai.gy = spot.y;
  h.ai.at = s.tick;
  return spot;
};

/** The sector whose gate is closest to a cell (lowest index on ties). */
const nearestGate = (s: BattleState, q: number, r: number): number => {
  let best = -1;
  let bd = 0;
  for (const sec of s.map.sectors) {
    const d = hexLen(sec.gate.q - q, sec.gate.r - r);
    if (best < 0 || d < bd) {
      best = sec.index;
      bd = d;
    }
  }
  return best;
};

const postFor = (s: BattleState, h: Hero): { x: number; y: number } => {
  if (h.post >= 0 && s.map.sectors[h.post]) return postSpot(s, h.post);
  // "Wherever needed": the sector whose gate has the most nightmares near it.
  let best = -1;
  let bestN = 0;
  for (const sec of s.map.sectors) {
    const g = hexToWorld(sec.gate.q, sec.gate.r);
    const n = countNear(s, g.x, g.y, 5000);
    if (n > bestN) {
      bestN = n;
      best = sec.index;
    }
  }
  return best >= 0 ? postSpot(s, best) : hexToWorld(0, 2);
};

const pick = (h: Hero, list: Enemy[], rule: Hero['profile']['targetPriority']): Enemy | null => {
  let best: Enemy | null = null;
  let bk = 0;
  for (const e of list) {
    let k: number;
    switch (rule) {
      case 'weakest':
        k = -e.hp;
        break;
      case 'strongest':
        k = e.maxHp * 4 + (e.elite ? 100000 : 0);
        break;
      case 'attackingStructures':
        k = (e.tgtKind === 2 ? 1000000000 : 0) - idiv(dist2(h.x, h.y, e.x, e.y), 1000);
        break;
      default:
        k = -dist2(h.x, h.y, e.x, e.y);
    }
    if (!best || k > bk) {
      best = e;
      bk = k;
    }
  }
  return best;
};

const threshold = (h: Hero, id: string, fallback: number): number => {
  const v = h.profile.skillThresholds[id];
  return v === undefined ? fallback : v < 1 ? 1 : v;
};

const canUlt = (h: Hero): boolean => h.relics.includes('vigilGrimoire') && (h.grimUsed ?? 0) < relicUses(h, 'vigilGrimoire');

const ult = (s: BattleState, h: Hero, x: number, y: number, id?: number): void => {
  if (!canUlt(h) || h.skillCd[2] > 0) return;
  if (castOwn(s, h, { t: 'skill', slot: 2, x, y, ...(id ? { id } : {}) })) h.grimUsed = (h.grimUsed ?? 0) + 1;
};

/** A slot carrying a learned skill is used by its tags (`useLearned`), never by the class's own code for that slot. */
const castOwn = (s: BattleState, h: Hero, c: Extract<Command, { t: 'skill' }>): boolean => kitOf(h)[c.slot] === HEROES[h.cls].skills[c.slot] && !spares(s, h, c) && castSkill(s, h, c);

/** A spectre never throws fire at the dragon of the Burn: it closes its scales (ai/dragon.ts). */
const spares = (s: BattleState, h: Hero, c: Extract<Command, { t: 'skill' }>): boolean => h.control === 'ghost' && fireOnDragon(s, h, c.slot, c.x, c.y, c.id);

/**
 * The learned skills (docs/PODERES.md §8): no code of their own here, they're used by their tags. An ultimate
 * only with the Vigil Grimoire, like every spectre's.
 */
const useLearned = (s: BattleState, h: Hero, t: Enemy): void => {
  const kit = kitOf(h);
  for (const slot of [0, 1, 2] as const) {
    const id = kit[slot] as (typeof kit)[number];
    if (id === HEROES[h.cls].skills[slot] || h.skillCd[slot] > 0) continue;
    if (slot === 2 && !canUlt(h)) continue;
    const sk = rankedSkill(SKILLS[id], h.ranks[slot] ?? 1, h.forks?.[slot] ?? null, h.glyphs?.[slot] ?? null);
    const ai = sk.ai;
    if (!ai) continue;
    const n = threshold(h, id, ai.n) + (slot === 2 ? GHOST_RESCUE.packExtra : 0);
    const foes = (x: number, y: number, r: number): number => countNear(s, x, y, r, (e) => targetable(s, e));
    let at: { x: number; y: number } | null = null;
    if (ai.use === 'pack') {
      if (withinRange(h.x, h.y, t.x, t.y, sk.castRange) && foes(t.x, t.y, sk.radius > 0 && sk.radius < 2500 ? sk.radius : 1500) >= n) at = { x: t.x, y: t.y };
    } else if (ai.use === 'around') {
      if (foes(h.x, h.y, sk.radius) >= n) at = { x: h.x, y: h.y };
    } else if (ai.use === 'fight') {
      // Aimed skills (a cone, a line) go at the nightmare being fought, not at the hero's own feet.
      if (foes(h.x, h.y, ai.reach ?? sk.radius) >= n) at = sk.targeting === 'point' ? { x: t.x, y: t.y } : { x: h.x, y: h.y };
    } else if (ai.use === 'heal') {
      const hurt = s.heroes.filter((o) => o.downTicks === 0 && o.hp * 10 < o.stats.maxHp * 7 && withinRange(h.x, h.y, o.x, o.y, sk.castRange));
      const worst = mostHurt(s, h.x, h.y, sk.castRange);
      if (hurt.length >= n && worst) at = { x: worst.id === h.id ? t.x : worst.x, y: worst.id === h.id ? t.y : worst.y };
    } else if (ai.use === 'repair') {
      const hurt = s.structures.filter((st) => {
        if (st.destroyed || st.maxHp <= 0 || st.hp * 2 >= st.maxHp) return false;
        const c = hexToWorld(st.q, st.r);
        return withinRange(h.x, h.y, c.x, c.y, ai.reach ?? sk.radius);
      }).length;
      if (hurt >= n) at = { x: h.x, y: h.y };
    } else {
      const pressed = s.heroes.filter((o) => o.downTicks === 0 && withinRange(h.x, h.y, o.x, o.y, sk.radius) && foes(o.x, o.y, 2500) > 0).length;
      if (pressed >= n) at = { x: h.x, y: h.y };
    }
    if (!at) continue;
    const target = sk.targeting === 'enemy' ? { id: t.id } : {};
    const cmd = { t: 'skill' as const, slot, x: at.x, y: at.y, ...target };
    if (!spares(s, h, cmd) && castSkill(s, h, cmd) && slot === 2) h.grimUsed = (h.grimUsed ?? 0) + 1;
  }
};

export const useSkills = (s: BattleState, h: Hero, t: Enemy): void => {
  const count = (x: number, y: number, r: number): number => countNear(s, x, y, r, (e) => targetable(s, e));
  if (h.kit) useLearned(s, h, t);
  switch (h.cls) {
    case 'warrior': {
      if (h.skillCd[0] === 0 && withinRange(h.x, h.y, t.x, t.y, h.forks?.[0] === 'bashCharge' ? FORKS.bashCharge.castRange : SKILLS.shieldBash.castRange)) {
        castOwn(s, h, { t: 'skill', slot: 0, id: t.id });
      }
      if (h.skillCd[1] === 0 && count(h.x, h.y, SKILLS.challenge.radius) >= threshold(h, 'challenge', 3)) {
        castOwn(s, h, { t: 'skill', slot: 1 });
      }
      if (count(h.x, h.y, SKILLS.oath.radius) >= threshold(h, 'oath', 5) + GHOST_RESCUE.packExtra) ult(s, h, h.x, h.y);
      break;
    }
    case 'artificer': {
      // A pack around one of its turrets: overload it. And drop a turret between the hero and the target.
      if (!h.overloadCd) {
        const ripe = s.turrets.find((x) => x.owner === h.id && !x.portable && count(x.x, x.y, ARTIFICER.overload.radius) >= threshold(h, 'overload', 3));
        if (ripe) overloadAt(s, h, ripe.x, ripe.y);
      }
      if (h.skillCd[0] === 0 && withinRange(h.x, h.y, t.x, t.y, SKILLS.turret.castRange + SKILLS.turret.radius)) {
        castOwn(s, h, { t: 'skill', slot: 0, x: (h.x + t.x) >> 1, y: (h.y + t.y) >> 1 });
      }
      if (h.skillCd[1] === 0 && withinRange(h.x, h.y, t.x, t.y, SKILLS.gearGrenade.castRange) && count(t.x, t.y, SKILLS.gearGrenade.radius) >= threshold(h, 'gearGrenade', 3)) {
        castOwn(s, h, { t: 'skill', slot: 1, x: t.x, y: t.y });
      }
      // The automaton goes down between the hero and the pack.
      if (count(t.x, t.y, 3000) >= threshold(h, 'automaton', 4) + GHOST_RESCUE.packExtra) ult(s, h, (h.x + t.x) >> 1, (h.y + t.y) >> 1);
      break;
    }
    case 'mage': {
      if (h.skillCd[0] === 0 && withinRange(h.x, h.y, t.x, t.y, SKILLS.fireball.castRange)) {
        if (count(t.x, t.y, SKILLS.fireball.radius) >= threshold(h, 'fireball', 2)) castOwn(s, h, { t: 'skill', slot: 0, x: t.x, y: t.y });
      }
      if (h.skillCd[1] === 0 && !isFlying(t) && count(t.x, t.y, 2000) >= 3 && withinRange(h.x, h.y, t.x, t.y, SKILLS.frostWall.castRange)) {
        castOwn(s, h, { t: 'skill', slot: 1, x: t.x, y: t.y });
      }
      if (count(t.x, t.y, SKILLS.storm.radius) >= threshold(h, 'storm', 4) + GHOST_RESCUE.packExtra) ult(s, h, t.x, t.y);
      break;
    }
    case 'cleric':
      // (Ghost clerics already healed this tick, at the top of ghostThink; the crypt bot heals here.)
      if (h.control !== 'ghost') clericCare(s, h);
      break;
    case 'barbarian': {
      if (h.skillCd[0] === 0 && count(h.x, h.y, SKILLS.whirl.radius) >= threshold(h, 'whirl', 2)) castOwn(s, h, { t: 'skill', slot: 0 });
      // The leap reaches the far ones (and lands in a pack).
      const far = !withinRange(h.x, h.y, t.x, t.y, 2200);
      if (h.skillCd[1] === 0 && withinRange(h.x, h.y, t.x, t.y, SKILLS.leap.castRange) && (far || count(t.x, t.y, SKILLS.leap.radius) >= threshold(h, 'leap', 3))) {
        castOwn(s, h, { t: 'skill', slot: 1, x: t.x, y: t.y });
      }
      // The frenzy for a big pack around it, not the first scuffle.
      if (count(h.x, h.y, 2500) >= threshold(h, 'frenzy', 5) + GHOST_RESCUE.packExtra) ult(s, h, h.x, h.y);
      break;
    }
    case 'paladin': {
      // The smite on the corrupt first.
      if (h.skillCd[0] === 0) {
        const bad = firstNear(s, h.x, h.y, SKILLS.smite.castRange, (e) => targetable(s, e) && corrupt(e));
        if (bad) castOwn(s, h, { t: 'skill', slot: 0, id: bad.id });
        else if (withinRange(h.x, h.y, t.x, t.y, SKILLS.smite.castRange)) castOwn(s, h, { t: 'skill', slot: 0, id: t.id });
      }
      // (Its aegis is raised at the top of ghostThink; the crypt bot raises it here.)
      if (h.skillCd[1] === 0 && h.control !== 'ghost') guard(s, h);
      if (count(t.x, t.y, SKILLS.hallow.radius) >= threshold(h, 'hallow', 4) + GHOST_RESCUE.packExtra) ult(s, h, t.x, t.y);
      break;
    }
    case 'druid': {
      if (h.skillCd[0] === 0) {
        // Flyers come down first; otherwise a pack.
        const flyer = firstNear(s, h.x, h.y, SKILLS.entangle.castRange, (e) => targetable(s, e) && isFlying(e));
        if (flyer) castOwn(s, h, { t: 'skill', slot: 0, x: flyer.x, y: flyer.y });
        else if (withinRange(h.x, h.y, t.x, t.y, SKILLS.entangle.castRange) && count(t.x, t.y, SKILLS.entangle.radius) >= threshold(h, 'entangle', 3)) castOwn(s, h, { t: 'skill', slot: 0, x: t.x, y: t.y });
      }
      // The bear when they crowd in on it; the bloom when friends around are low.
      if (h.skillCd[1] === 0 && count(h.x, h.y, 1800) >= threshold(h, 'bearForm', 3)) castOwn(s, h, { t: 'skill', slot: 1 });
      if (bloomPays(s, h)) ult(s, h, h.x, h.y);
      break;
    }
    case 'ranger': {
      if (h.skillCd[0] === 0 && withinRange(h.x, h.y, t.x, t.y, SKILLS.multishot.castRange)) {
        if (count(t.x, t.y, SKILLS.multishot.radius) >= threshold(h, 'multishot', 2)) castOwn(s, h, { t: 'skill', slot: 0, x: t.x, y: t.y });
      }
      if (h.skillCd[1] === 0 && (t.elite || t.maxHp >= 150 || t.kind === 'witch' || t.kind === 'shell')) {
        castOwn(s, h, { t: 'skill', slot: 1, id: t.id });
      }
      if (count(t.x, t.y, SKILLS.arrowRain.radius) >= threshold(h, 'arrowRain', 4) + GHOST_RESCUE.packExtra) ult(s, h, t.x, t.y);
      break;
    }
  }
};

/** The nearest of `list` that `ok` picks, or null. */
const nearestWith = (h: Hero, list: Enemy[], ok: (e: Enemy) => boolean): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    if (!ok(e)) continue;
    const d = dist2(h.x, h.y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

/** What a barbarian ghost goes out after: archers, slingers, siege and elites, the nearest, unless a crowd stands around it. */
const hunt = (s: BattleState, h: Hero, post: { x: number; y: number }, leash: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of enemiesNear(s, post.x, post.y, leash + 3000, (o) => (o.kind === 'archer' || o.kind === 'slinger' || !!NIGHTMARES[o.kind].siege || o.elite) && targetable(s, o) && !isFlying(o))) {
    if (countNear(s, e.x, e.y, 2000, (o) => targetable(s, o), 6) >= 6) continue;
    const d = dist2(h.x, h.y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

/**
 * The nearest siege engine standing out there throwing at the bastion (the mangonel), within reach of a
 * sortie, that no other hero is already after: one goes out per engine, the rest keep the walls.
 */
const siegeOut = (s: BattleState, h: Hero, post: { x: number; y: number }): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  const taken = (e: Enemy): boolean => s.heroes.some((o) => o.id !== h.id && o.target === e.id && o.downTicks === 0);
  for (const e of enemiesNear(s, post.x, post.y, GHOST_GUARD.siegeHunt, (o) => !!NIGHTMARES[o.kind].lobs && targetable(s, o) && !taken(o))) {
    const d = dist2(h.x, h.y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

/**
 * A paladin ghost's aegis: on the friend taking a beating, else on the building about to fall (gates
 * first), else on the keep if it is under blows.
 */
const guard = (s: BattleState, h: Hero): void => {
  const r = SKILLS.aegis.castRange;
  const friend = mostHurt(s, h.x, h.y, r);
  if (friend && missingBp(friend) >= 3500 && countNear(s, friend.x, friend.y, 2000, (e) => targetable(s, e), 1) > 0) {
    castOwn(s, h, { t: 'skill', slot: 1, id: friend.id });
    return;
  }
  let best: { id: number; k: number } | null = null;
  for (const st of s.structures) {
    if (st.destroyed || st.maxHp <= 0 || st.hp * 2 > st.maxHp) continue;
    const c = structurePos(st);
    if (!withinRange(h.x, h.y, c.x, c.y, r)) continue;
    if (!s.enemies.some((e) => e.hp > 0 && e.tgtKind === 2 && e.tgtId === st.id)) continue;
    const k = (st.type === 'gate' ? 100000 : 0) - idiv(st.hp * 10000, st.maxHp);
    if (!best || k > best.k) best = { id: st.id, k };
  }
  if (best) {
    castOwn(s, h, { t: 'skill', slot: 1, structure: best.id });
    return;
  }
  if (s.map.kind === 'bastion' && withinRange(h.x, h.y, 0, 0, r) && s.enemies.filter((e) => e.hp > 0 && e.tgtKind === 3).length >= 2) castOwn(s, h, { t: 'skill', slot: 1, keep: true });
};

/** Share of life missing (bp). */
const missingBp = (o: Hero): number => (o.downTicks > 0 || o.stats.maxHp <= 0 ? 0 : idiv((o.stats.maxHp - o.hp) * 10000, o.stats.maxHp));

/**
 * A cleric's spectre heals with sense: mends the most hurt (the worse off, the sooner), lays a
 * sanctuary where two or more are hurt together, and saves dawn for the fallen or a group in trouble.
 */
export const clericCare = (s: BattleState, h: Hero, ghost = false): void => {
  if (h.skillCd[0] === 0) {
    const tgt = mostHurt(s, h.x, h.y, SKILLS.mend.castRange);
    if (tgt && missingBp(tgt) >= (ghost ? MEND_AT_BP : 2500)) castOwn(s, h, { t: 'skill', slot: 0, id: tgt.id });
    else {
      // Nobody needs it: the mend inspires whoever is fighting the most.
      const fighter = inspireTarget(s, h);
      if (fighter) castOwn(s, h, { t: 'skill', slot: 0, id: fighter.id });
    }
  }
  if (h.skillCd[1] === 0) {
    const r = SKILLS.sanctuary.radius;
    for (const o of s.heroes) {
      if (o.downTicks > 0 || !withinRange(h.x, h.y, o.x, o.y, SKILLS.sanctuary.castRange)) continue;
      const hurt = s.heroes.filter((x) => x.downTicks === 0 && missingBp(x) >= 2000 && withinRange(o.x, o.y, x.x, x.y, r)).length;
      const pressed = missingBp(o) >= 4000 && countNear(s, o.x, o.y, 2500, (e) => targetable(s, e), 2) >= 2;
      if (hurt >= 2 || pressed) {
        castOwn(s, h, { t: 'skill', slot: 1, x: o.x, y: o.y });
        break;
      }
    }
  }
  const near = s.heroes.filter((o) => withinRange(h.x, h.y, o.x, o.y, SKILLS.dawn.radius));
  // A ghost's one dawn (the Vigil Grimoire) is kept for the fallen; a bot in the crypt also spends it on a group in trouble.
  if (near.some((o) => o.downTicks >= GHOST_RESCUE.minDownTicks) || (!ghost && near.filter((o) => o.downTicks === 0 && missingBp(o) >= 6000).length >= 2)) ult(s, h, h.x, h.y);
};

/** The ally (not the cleric) in mend's reach, not yet inspired, with the most nightmares around it (at least two). */
const inspireTarget = (s: BattleState, h: Hero): Hero | null => {
  let best: Hero | null = null;
  let most = 1;
  for (const o of s.heroes) {
    if (o.id === h.id || o.downTicks > 0 || (o.inspire ?? 0) > 0 || !withinRange(h.x, h.y, o.x, o.y, SKILLS.mend.castRange)) continue;
    const n = countNear(s, o.x, o.y, INSPIRE_PRESS, (e) => targetable(s, e));
    if (n > most) {
      most = n;
      best = o;
    }
  }
  return best;
};

/** How close nightmares count as fighting the one a ghost cleric would inspire. */
const INSPIRE_PRESS = 2000;

/** Whether the druid's bloom would save someone: two heroes in its reach under 60 % of their life, or one under 30 %. */
const bloomPays = (s: BattleState, h: Hero): boolean => {
  let low = 0;
  for (const o of s.heroes) {
    if (o.downTicks > 0 || !withinRange(h.x, h.y, o.x, o.y, SKILLS.bloom.radius)) continue;
    const m = missingBp(o);
    if (m >= 7000) return true;
    if (m >= 4000) low++;
  }
  return low >= 2;
};

/** A ghost cleric mends whoever has lost this much (bp). */
const MEND_AT_BP = 1500;

/** A friend in trouble and what is on them. */
interface Trouble {
  o: Hero;
  foes: number;
  elite: boolean;
}

/**
 * Who could use this ghost's ultimate: friends down for a while yet, or under a quarter of their life
 * with nightmares on them; none another ghost is already on its way to.
 */
const troubles = (s: BattleState, h: Hero): Trouble[] => {
  const out: Trouble[] = [];
  for (const o of s.heroes) {
    if (o.id === h.id) continue;
    const fallen = o.downTicks >= GHOST_RESCUE.minDownTicks;
    const low = o.downTicks === 0 && o.stats.maxHp > 0 && o.hp * 10000 < o.stats.maxHp * GHOST_RESCUE.lowHpBp;
    if (!fallen && !low) continue;
    if (s.heroes.some((x) => x.id !== h.id && x.ai.rescue === o.id && x.downTicks === 0)) continue;
    const near = enemiesNear(s, o.x, o.y, GHOST_RESCUE.pressR, (e) => targetable(s, e) && e.kind !== 'boneWall');
    const elite = near.some((e) => e.elite);
    if (low && near.length < 2 && !elite) continue;
    out.push({ o, foes: near.length, elite });
  }
  return out;
};

/**
 * The Vigil Grimoire's ultimate, saved for a friend in trouble. The cleric raises the fallen or saves the
 * one about to fall; the warrior takes the oath beside them; mage and ranger rain on what is there from
 * where they stand; the artificer drops the automaton between. Damage and oath only for a real pack or an
 * elite. The ones that must be close walk up to eight cells, never away from a busier spot of their own,
 * and a cleric only if it arrives before the friend would get up anyway. True while it is on it.
 */
const rescue = (s: BattleState, h: Hero, post: { x: number; y: number }): boolean => {
  if (!canUlt(h) || h.skillCd[2] > 0) {
    h.ai.rescue = undefined;
    return false;
  }
  const busyHere = countNear(s, post.x, post.y, THREAT_R, (e) => e.hp > 0 && e.kind !== 'boneWall');
  let best: { tr: Trouble; reach: number; d: number } | null = null;
  for (const tr of troubles(s, h)) {
    const { o } = tr;
    const pays = tr.foes >= GHOST_RESCUE.minFoes || tr.elite;
    // How close the ghost must stand to the friend for its ultimate to land there.
    let reach: number;
    switch (h.cls) {
      case 'cleric':
        reach = SKILLS.dawn.radius - 800;
        break;
      case 'warrior':
        if (!pays) continue;
        reach = SKILLS.oath.radius - 1200;
        break;
      // The barbarian and the druid stand beside the friend: a war cry, a dire bear.
      case 'barbarian':
        if (!pays) continue;
        reach = FORKS.warpath.radius - 1200;
        break;
      case 'druid':
        if (!pays) continue;
        reach = FORKS.direBear.taunt - 1200;
        break;
      case 'paladin':
        if (!pays) continue;
        reach = SKILLS.hallow.castRange;
        break;
      case 'artificer':
        if (!pays) continue;
        reach = SKILLS.automaton.castRange;
        break;
      case 'mage':
        if (!pays) continue;
        reach = SKILLS.storm.castRange;
        break;
      default:
        if (!pays) continue;
        reach = SKILLS.arrowRain.castRange;
    }
    const d = len(o.x - h.x, o.y - h.y);
    if (d > reach) {
      // Casters throw from where they stand; the rest walk, but not far, and not off a busier spot.
      if (h.cls === 'mage' || h.cls === 'ranger' || h.cls === 'paladin') continue;
      if (d - reach > GHOST_RESCUE.walkMax || busyHere > tr.foes + 1) continue;
      if (o.downTicks > 0 && h.cls === 'cleric' && idiv((d - reach) * 12, h.stats.speed > 0 ? h.stats.speed : 1) + 12 >= o.downTicks) continue;
    }
    if (!best || d < best.d) best = { tr, reach, d };
  }
  if (!best) {
    h.ai.rescue = undefined;
    return false;
  }
  const { o } = best.tr;
  if (best.d > best.reach) {
    h.ai.rescue = o.id;
    h.target = 0;
    if (!h.moving || s.tick % 12 === 0) orderMove(s, h, o.x, o.y);
    return true;
  }
  h.ai.rescue = undefined;
  if (h.cls === 'cleric' || h.cls === 'warrior' || h.cls === 'barbarian' || h.cls === 'druid') ult(s, h, h.x, h.y);
  else if (h.cls === 'artificer') ult(s, h, (h.x + o.x) >> 1, (h.y + o.y) >> 1);
  else ult(s, h, o.x, o.y);
  return (h.grimUsed ?? 0) > 0;
};

/** Between packs: clerics keep healing (artificers mend whatever stands near them on their own). */
const useIdleSkills = (s: BattleState, h: Hero): void => {
  if (h.cls === 'cleric' && h.control !== 'ghost') clericCare(s, h);
};

/** Ghost damage multiplier: 70 % × activity weight (SPEC §9). */
export const ghostPowerBp = (activityBp: number, basePowerBp: number): number => mulBp(basePowerBp, activityBp);

